import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';
import { ingestorsApi } from './ingestors/api';
import type { IngestorActor, IngestorInstance, UnmappedActor } from './ingestors/types';

interface User {
  id: string;
  handle: string;
  display_name: string;
  email: string;
}

export function IngestorActorsPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const inst = useQuery({
    queryKey: ['ingestor-instance', id],
    queryFn: () => ingestorsApi.getInstance(id),
    enabled: !!id,
  });
  const actors = useQuery({
    queryKey: ['ingestor-actors', id],
    queryFn: () => ingestorsApi.listActors(id),
    enabled: !!id,
  });
  const unmapped = useQuery({
    queryKey: ['ingestor-unmapped-actors', id],
    queryFn: () => ingestorsApi.listUnmappedActors(id),
    enabled: !!id,
    refetchInterval: 15_000,
  });
  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => api<{ users: User[] }>('/api/users'),
  });

  const createMapping = useMutation({
    mutationFn: (body: { external_id: string; user_id: string }) =>
      ingestorsApi.createActor(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ingestor-actors', id] });
      qc.invalidateQueries({ queryKey: ['ingestor-unmapped-actors', id] });
    },
  });
  const remove = useMutation({
    mutationFn: (externalId: string) => ingestorsApi.deleteActor(id, externalId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ingestor-actors', id] });
      qc.invalidateQueries({ queryKey: ['ingestor-unmapped-actors', id] });
    },
  });
  // Create a new Mirabili user, then immediately map this actor to them.
  const createAndMap = useMutation({
    mutationFn: async ({ actor, newUser }: { actor: UnmappedActor; newUser: { display_name: string; handle: string; email: string; password: string; is_ignored: boolean } }) => {
      const { user } = await api<{ user: User }>('/api/users', { method: 'POST', body: { ...newUser, role: 'player' } });
      await ingestorsApi.createActor(id, { external_id: actor.external_id, user_id: user.id });
      return user;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['ingestor-actors', id] });
      qc.invalidateQueries({ queryKey: ['ingestor-unmapped-actors', id] });
    },
  });

  const [err, setErr] = useState<string | null>(null);
  const [manualExt, setManualExt] = useState('');
  const [manualUser, setManualUser] = useState('');
  // Per-unmapped-actor inline "create user" form state.
  const [creatingFor, setCreatingFor] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newHandle, setNewHandle] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPass, setNewPass] = useState('');
  const [newIgnored, setNewIgnored] = useState(false);

  if (inst.isLoading) return <p className="text-ink-muted">Loading…</p>;
  if (!inst.data) return <p className="text-accent-danger">Failed to load ingestor.</p>;
  const i: IngestorInstance = inst.data.instance;
  const userById = (uid: string) => (users.data?.users ?? []).find((u) => u.id === uid);

  const startCreateFor = (a: UnmappedActor) => {
    setCreatingFor(a.external_id);
    setNewName(a.actor_name ?? '');
    setNewHandle(suggestHandle(a.actor_name ?? a.external_id));
    setNewEmail(''); setNewPass('');
  };

  const submitCreate = async (a: UnmappedActor) => {
    setErr(null);
    if (!newName.trim() || !newHandle.trim() || !newEmail.trim() || !newPass.trim()) {
      setErr('All fields required to create a user.'); return;
    }
    try {
      await createAndMap.mutateAsync({ actor: a, newUser: { display_name: newName, handle: newHandle, email: newEmail, password: newPass, is_ignored: newIgnored } });
      setCreatingFor(null);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to create user + map.');
    }
  };

  const mapExisting = async (externalId: string, userId: string) => {
    setErr(null);
    if (!userId) return;
    try {
      await createMapping.mutateAsync({ external_id: externalId, user_id: userId });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to map.');
    }
  };

  const unmappedList = unmapped.data?.unmapped ?? [];

  return (
    <div className="space-y-6">
      <header>
        <Link to={`/admin/ingestors/${i.id}`} className="text-sm text-ink-muted hover:text-ink">← {i.name}</Link>
        <h1 className="mt-1 text-2xl font-semibold">Actor mappings</h1>
        <p className="text-ink-muted">
          Events from unmapped actors are recorded but don't award XP. Auto-discovered actors
          appear below — map them to an existing user or create a new one.
        </p>
      </header>

      {err ? <p className="text-sm text-accent-danger" role="alert">{err}</p> : null}

      {/* Unmapped actors — auto-discovered from events */}
      <div className="card">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">
          Unmapped actors ({unmappedList.length})
        </h2>
        <p className="mb-3 text-xs text-ink-dim">Auto-refreshes every 15s. Discovered from recent events.</p>
        {unmappedList.length === 0 ? (
          <p className="text-sm text-ink-muted">No unmapped actors — everyone is mapped. 🎉</p>
        ) : (
          <ul className="space-y-2">
            {unmappedList.map((a) => (
              <li key={a.external_id} className="rounded-md bg-bg-elevated p-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-medium">
                      {a.actor_name ?? 'Unknown'}
                      {a.actor_name ? <span className="ml-2 font-mono text-xs text-ink-dim">{a.external_id}</span> : <span className="ml-2 font-mono text-xs">{a.external_id}</span>}
                    </p>
                    <p className="text-xs text-ink-dim">
                      {a.event_count} event{a.event_count === 1 ? '' : 's'} · last {new Date(a.last_seen).toLocaleString()}
                      {a.sample_target ? ` · e.g. ${a.sample_target}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Map to existing user */}
                    <select
                      className="input max-w-[14rem]"
                      value=""
                      onChange={(e) => { if (e.target.value) mapExisting(a.external_id, e.target.value); }}
                      disabled={createMapping.isPending}
                    >
                      <option value="">Map to…</option>
                      {(users.data?.users ?? []).map((u) => (
                        <option key={u.id} value={u.id}>{u.display_name} (@{u.handle})</option>
                      ))}
                    </select>
                    {/* Or create a new user */}
                    <button className="btn" onClick={() => startCreateFor(a)} disabled={creatingFor === a.external_id}>
                      + Create user
                    </button>
                  </div>
                </div>
                {creatingFor === a.external_id ? (
                  <div className="mt-3 grid gap-2 rounded-md bg-bg-panel p-3 sm:grid-cols-2">
                    <input className="input" placeholder="Display name" value={newName} onChange={(e) => setNewName(e.target.value)} />
                    <input className="input font-mono" placeholder="handle" value={newHandle} onChange={(e) => setNewHandle(e.target.value.toLowerCase())} />
                    <input className="input" placeholder="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
                    <input className="input" placeholder="password (min 8)" type="password" value={newPass} onChange={(e) => setNewPass(e.target.value)} />
                    <label className="sm:col-span-2 inline-flex items-center gap-2 text-sm text-ink-muted">
                      <input type="checkbox" checked={newIgnored} onChange={(e) => setNewIgnored(e.target.checked)} className="h-4 w-4" />
                      Mark as ignored (non-player / service account — no XP, no public board)
                    </label>
                    <div className="sm:col-span-2 flex justify-end gap-2">
                      <button className="btn" onClick={() => setCreatingFor(null)}>Cancel</button>
                      <button className="btn-primary" onClick={() => submitCreate(a)} disabled={createAndMap.isPending}>
                        {createAndMap.isPending ? 'Creating…' : 'Create + map'}
                      </button>
                    </div>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Manual mapping (for ids not yet in events) */}
      <div className="card">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">Manual mapping</h2>
        <form
          onSubmit={async (e) => {
            e.preventDefault(); setErr(null);
            try {
              await createMapping.mutateAsync({ external_id: manualExt.trim(), user_id: manualUser });
              setManualExt(''); setManualUser('');
            } catch (e2) { setErr(e2 instanceof ApiError ? e2.message : 'Failed to create mapping.'); }
          }}
          className="mt-2 grid gap-3 sm:grid-cols-3"
        >
          <input className="input font-mono" placeholder="external id" value={manualExt} onChange={(e) => setManualExt(e.target.value)} required />
          <select className="input" value={manualUser} onChange={(e) => setManualUser(e.target.value)} required>
            <option value="">Select user…</option>
            {(users.data?.users ?? []).map((u) => <option key={u.id} value={u.id}>{u.display_name} (@{u.handle})</option>)}
          </select>
          <button type="submit" className="btn-primary" disabled={createMapping.isPending}>{createMapping.isPending ? 'Mapping…' : 'Add mapping'}</button>
        </form>
      </div>

      {/* Mapped actors */}
      <div className="card overflow-x-auto">
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-ink-muted">Mapped ({(actors.data?.actors ?? []).length ?? 0})</h2>
        <table className="w-full text-sm">
          <thead className="text-left text-ink-muted">
            <tr><th className="py-2">External id</th><th>User</th><th>Email</th><th>Created</th><th></th></tr>
          </thead>
          <tbody>
            {(actors.data?.actors ?? []).map((a: IngestorActor) => {
              const u = userById(a.user_id);
              return (
                <tr key={a.external_id} className="border-t border-white/5">
                  <td className="py-2 font-mono">{a.external_id}</td>
                  <td>{u ? u.display_name : <span className="text-accent-danger">missing</span>}</td>
                  <td className="text-ink-muted">{u?.email ?? '—'}</td>
                  <td className="text-ink-muted">{new Date(a.created_at).toLocaleDateString()}</td>
                  <td className="text-right"><button className="btn-danger" onClick={() => remove.mutate(a.external_id)}>Delete</button></td>
                </tr>
              );
            })}
            {!(actors.data?.actors ?? []).length ? (
              <tr><td colSpan={5} className="py-4 text-center text-ink-muted">No mappings yet.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Suggest a handle from a display name or raw id. Lowercase, strip
// spaces/punctuation, take the first ~20 chars.
function suggestHandle(nameOrId: string): string {
  const base = nameOrId
    .replace(/^.*:/, '') // strip Jira accountId prefix like "557058:uuid"
    .replace(/[^a-zA-Z0-9_-]/g, '')
    .toLowerCase()
    .slice(0, 20);
  return base || 'newuser';
}